import type { Listing, ListingUrlSyncConfig } from '@super/shared-types'
import { ListingType } from '@super/shared-types'
import { DataTable } from '@/components/directory/DataTable'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Building2, DollarSign, Home, MapPin, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/utils/currency'
import { formatDate } from '@/utils/date'
import { FloatingActionIslandProvider } from '@/contexts/FloatingActionIslandContext'
import { FloatingActionIsland } from '@/components/ui/FloatingActionIsland'
import { ListingsFallbackLinkControl } from '@/components/settings/ListingsFallbackLinkControl'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useState } from 'react'
import { useMediaQuery } from '@/hooks/use-mobile'

interface ListingsTabProps {
  listings: Listing[]
  loading: boolean
  syncConfig?: ListingUrlSyncConfig | null
  /** Company-level listings fallback link (source of truth), or null when unset. */
  fallbackLink?: string | null
  fallbackLinkLoading?: boolean
  /** Persist a new fallback link. Pass null to clear it. Throws on failure. */
  onFallbackLinkSave?: (link: string | null) => Promise<void>
}

function getListingTypeLabel(type: ListingType | null | undefined): string {
  if (!type) return 'N/A'
  switch (type) {
    case ListingType.APARTMENT:
      return 'Apartment'
    case ListingType.CONDO:
      return 'Condo'
    case ListingType.HOUSE:
      return 'House'
    case ListingType.TOWNHOUSE:
      return 'Townhouse'
    default:
      return 'N/A'
  }
}

function TruncatedTextWithTooltip({
  text,
  maxLength = 30,
  isUrl = false,
}: {
  text: string
  maxLength?: number
  isUrl?: boolean
}) {
  const truncated =
    text.length > maxLength ? `${text.slice(0, maxLength)}...` : text
  const needsTooltip = text.length > maxLength

  const content = isUrl ? (
    <a
      href={text}
      target="_blank"
      rel="noopener noreferrer"
      className="text-xs text-muted-foreground underline hover:text-foreground"
    >
      {truncated}
    </a>
  ) : (
    <span className="text-xs text-muted-foreground">{truncated}</span>
  )

  if (!needsTooltip) {
    return content
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">{content}</span>
      </TooltipTrigger>
      <TooltipContent
        className="max-w-[240px] w-fit bg-gray-900 text-white"
        side="top"
      >
        {isUrl ? (
          <span className="text-xs break-all">{text}</span>
        ) : (
          <span className="text-xs break-words">{text}</span>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

function ListingImageTooltip({ imgUrl, alt }: { imgUrl: string; alt: string }) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [dimensions, setDimensions] = useState({ width: 400, height: 300 })

  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const aspectRatio = img.naturalWidth / img.naturalHeight

    let width = img.naturalWidth
    let height = img.naturalHeight

    // Constrain to max dimensions while maintaining aspect ratio
    if (width > 400 || height > 300) {
      if (aspectRatio > 400 / 300) {
        width = 400
        height = 400 / aspectRatio
      } else {
        height = 300
        width = 300 * aspectRatio
      }
    }

    setDimensions({ width, height })
    setIsLoaded(true)
  }

  return (
    <div
      className="relative transition-all duration-300 ease-out rounded-lg overflow-hidden shadow-lg"
      style={{
        width: `${dimensions.width}px`,
        height: `${dimensions.height}px`,
      }}
    >
      {!isLoaded && <Skeleton className="absolute inset-0 w-full h-full" />}
      <img
        src={imgUrl}
        alt={alt}
        className={cn(
          'w-full h-full object-cover transition-opacity duration-300 ease-out',
          isLoaded ? 'opacity-100' : 'opacity-0 absolute',
        )}
        onLoad={handleImageLoad}
        onError={e => {
          e.currentTarget.style.display = 'none'
        }}
      />
    </div>
  )
}

function AddressCell({ listing }: { listing: Listing }) {
  const hasImage = !!listing.imgUrl
  const isLargeScreen = useMediaQuery('lg')

  const handleMouseEnter = () => {
    if (hasImage) {
      // Start preloading the image on hover
      const img = new Image()
      img.src = listing.imgUrl!
    }
  }

  const addressContent = (
    <div className="flex items-start space-x-2">
      <MapPin
        className={cn(
          'h-4 w-4 mt-0.5 flex-shrink-0',
          hasImage ? 'text-[#4E9680]' : 'text-muted-foreground',
        )}
      />
      <div className="min-w-0">
        <div className="font-medium text-foreground truncate">
          {listing.address}
        </div>
        {listing.unit && (
          <div className="text-xs text-muted-foreground">
            Unit {listing.unit}
          </div>
        )}
      </div>
    </div>
  )

  if (!hasImage) {
    return addressContent
  }

  return (
    <Tooltip delayDuration={500} disableHoverableContent={true}>
      <TooltipTrigger asChild>
        <div className="cursor-pointer" onMouseEnter={handleMouseEnter}>
          {addressContent}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side={isLargeScreen ? 'right' : 'top'}
        sideOffset={0}
        className="p-0 border-0 bg-transparent shadow-none overflow-visible"
      >
        <ListingImageTooltip imgUrl={listing.imgUrl!} alt={listing.address} />
      </TooltipContent>
    </Tooltip>
  )
}

export function ListingsTab({
  listings,
  loading,
  syncConfig,
  fallbackLink = null,
  fallbackLinkLoading = false,
  onFallbackLinkSave,
}: ListingsTabProps) {
  const listingColumns = [
    {
      header: 'Address',
      minWidth: '300px',
      className: 'max-w-0',
      cell: (listing: Listing) => <AddressCell listing={listing} />,
    },
    {
      header: 'Type',
      cell: (listing: Listing) => (
        <div className="flex items-center space-x-1">
          <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="text-sm text-foreground">
            {getListingTypeLabel(listing.type)}
          </span>
        </div>
      ),
    },
    {
      header: 'Details',
      cell: (listing: Listing) => {
        const sqft = Number(listing.sqft)
        const hasSqft = !isNaN(sqft) && sqft > 0

        return (
          <div className="flex items-center space-x-3 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Home className="h-3.5 w-3.5 text-muted-foreground" />
              <span>
                {listing.beds} bed{listing.beds !== 1 ? 's' : ''}
              </span>
            </div>
            <span className="text-border">•</span>
            <span>
              {listing.baths} bath{listing.baths !== 1 ? 's' : ''}
            </span>
            {hasSqft && (
              <>
                <span className="text-border">•</span>
                <span>{sqft.toLocaleString()} sqft</span>
              </>
            )}
          </div>
        )
      },
    },
    {
      header: 'Rent',
      cell: (listing: Listing) => (
        <div className="flex items-center space-x-1">
          <DollarSign className="h-3.5 w-3.5 text-muted-foreground" />
          <span className="font-semibold text-foreground">
            {formatCurrency(listing.monthlyRentDollars)}
            <span className="text-xs text-gray-500 font-normal">/mo</span>
          </span>
        </div>
      ),
    },
    {
      header: 'Availability',
      cell: (listing: Listing) => (
        <div className="flex flex-col space-y-1">
          <Badge
            variant={listing.isAvailable ? 'default' : 'secondary'}
            className={cn(
              'w-fit',
              listing.isAvailable
                ? 'bg-success-muted text-success hover:bg-success-muted'
                : 'bg-muted text-muted-foreground hover:bg-muted',
            )}
          >
            {listing.isAvailable ? 'Available' : 'Unavailable'}
          </Badge>
          {listing.isAvailable && listing.availabilityDate && (
            <span className="text-xs text-muted-foreground">
              {formatDate(listing.availabilityDate)}
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'Pets',
      cell: (listing: Listing) => {
        const pets: string[] = []
        if (listing.dogsAllowed) pets.push('Dogs')
        if (listing.catsAllowed) pets.push('Cats')

        return (
          <div className="text-sm text-muted-foreground">
            {pets.length > 0 ? pets.join(', ') : 'No pets'}
          </div>
        )
      },
    },
    {
      header: 'Pet Policy',
      cell: (listing: Listing) =>
        listing.petPolicy ? (
          <TruncatedTextWithTooltip text={listing.petPolicy} maxLength={40} />
        ) : (
          <span className="text-xs text-muted-foreground/60">N/A</span>
        ),
    },
    {
      header: 'Parking',
      cell: (listing: Listing) => (
        <Badge
          variant="outline"
          className={cn(
            'w-fit',
            listing.hasParking ? 'border-success-border text-success' : '',
          )}
        >
          {listing.hasParking ? 'Yes' : 'No'}
        </Badge>
      ),
    },
    {
      header: 'Housing Vouchers',
      // Tri-state: undefined/null = not mentioned on the listing (distinct from "No").
      cell: (listing: Listing) => (
        <div className="flex justify-center">
          {listing.housingVouchersAccepted === undefined ||
          listing.housingVouchersAccepted === null ? (
            <span className="text-xs text-muted-foreground/60">Not listed</span>
          ) : (
            <Badge
              variant="outline"
              className={cn(
                'w-fit',
                listing.housingVouchersAccepted
                  ? 'border-success-border text-success'
                  : '',
              )}
            >
              {listing.housingVouchersAccepted ? 'Accepted' : 'Not accepted'}
            </Badge>
          )}
        </div>
      ),
    },
    {
      header: 'Details URL',
      cell: (listing: Listing) =>
        listing.detailsUrl ? (
          <TruncatedTextWithTooltip text={listing.detailsUrl} isUrl={true} />
        ) : (
          <span className="text-xs text-muted-foreground/60">N/A</span>
        ),
    },
    {
      header: 'Showing URL',
      cell: (listing: Listing) =>
        listing.showingUrl ? (
          <TruncatedTextWithTooltip text={listing.showingUrl} isUrl={true} />
        ) : (
          <span className="text-xs text-muted-foreground/60">N/A</span>
        ),
    },
  ]

  const showErrorNotification =
    syncConfig?.lastSyncStatus === 'failed' && syncConfig?.lastSyncError

  return (
    <TooltipProvider delayDuration={100}>
      <FloatingActionIslandProvider>
        <div className="space-y-4 pb-20">
          {/* Company-level fallback link — sits between the toolbar and the
              table and is always available, even before any listings sync. */}
          {onFallbackLinkSave && (
            <ListingsFallbackLinkControl
              fallbackLink={fallbackLink}
              loading={fallbackLinkLoading}
              onSave={onFallbackLinkSave}
            />
          )}

          {loading ? (
            <div className="border rounded-lg p-8">
              <div className="flex items-center justify-center">
                <Skeleton className="h-4 w-32" />
              </div>
            </div>
          ) : listings.length === 0 ? (
            <EmptyState
              icon={<Building2 className="h-8 w-8" />}
              title="No listings added"
              description="Connect your listings website and we'll sync and update your listings automatically."
            />
          ) : (
            <>
              {showErrorNotification && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-red-900 mb-1">
                        Last sync failed
                      </h4>
                      <p className="text-sm text-red-800">
                        {syncConfig.lastSyncError}
                      </p>
                      {syncConfig.lastSyncAt && (
                        <p className="text-xs text-red-600 mt-2">
                          Last attempted: {formatDate(syncConfig.lastSyncAt)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <DataTable
                data={listings}
                columns={listingColumns}
                itemType="listings"
                isDragActive={false}
                dropzoneProps={{}}
              />
            </>
          )}
          <FloatingActionIsland />
        </div>
      </FloatingActionIslandProvider>
    </TooltipProvider>
  )
}
